import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_PHONE_SCHEMA } from '../constants/site';

const BASE_URL = 'https://www.flashfunding.loan';
const SITE_NAME = 'Flash Funding';
const SCHEMA_PHONE = SITE_PHONE_SCHEMA;

export default function SEOHead({ 
  title, 
  description, 
  canonical,
  keywords,
  schema = [],
  breadcrumbs = [],
  faqs = [],
  noindex = false
}) {
  const { pathname } = useLocation();
  const hasSiteName = !!title && title.toLowerCase().includes(SITE_NAME.toLowerCase());
  const fullTitle = title ? (hasSiteName ? title : `${title} | ${SITE_NAME}`) : `${SITE_NAME} | Commercial Asset Finance Australia`;
  const canonicalPath = canonical || (pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname) || '/';
  const fullCanonical = canonicalPath.startsWith('http')
    ? canonicalPath
    : `${BASE_URL}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;
  const safeDescription = description || 'Commercial asset finance solutions for Australian businesses.';

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (attr, attrVal, content) => {
      let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', safeDescription);
    if (keywords) setMeta('name', 'keywords', keywords);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', safeDescription);
    setMeta('property', 'og:url', fullCanonical);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', safeDescription);
    let robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, follow');
    } else if (robots) {
      robots.remove();
    }

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', fullCanonical);

    // Schema markup
    const schemas = [
      // Organization (always)
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": SITE_NAME,
        "url": BASE_URL,
        "telephone": SCHEMA_PHONE,
        "address": { "@type": "PostalAddress", "addressCountry": "AU" },
        "sameAs": []
      },
      // FinancialService
      {
        "@context": "https://schema.org",
        "@type": "FinancialService",
        "name": SITE_NAME,
        "url": BASE_URL,
        "logo": `${BASE_URL}/logo.png`,
        "telephone": SCHEMA_PHONE,
        "email": "hello@flashfunding.loan",
        "areaServed": "AU",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "AU",
          "addressRegion": "NSW"
        },
        "description": "Australian finance broker arranging asset finance, equipment finance, business loans, car loans and personal loans through a panel of bank and non-bank lenders.",
        "sameAs": []
      },
      // Breadcrumb
      ...(breadcrumbs.length > 0 ? [{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((bc, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": bc.name,
          "item": `${BASE_URL}${bc.path}`
        }))
      }] : []),
      // FAQ
      ...(faqs.length > 0 ? [{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      }] : []),
      // Custom schemas
      ...schema
    ];

    // Remove old schema scripts
    document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());

    // Add new ones
    schemas.forEach(s => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());
    };
  }, [fullTitle, safeDescription, fullCanonical, keywords, breadcrumbs, faqs, schema, noindex]);

  return null;
}

export { BASE_URL, SITE_NAME };
